/**
 * DOM modal helpers for realtime lifecycle events.
 *
 * Deliberately free of React: these run out-of-band during an active Colyseus
 * session and must block / unblock independently of React render cycles. We
 * keep the useWorldRoom effect free of inline HTML.
 */
import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { RefreshCw } from 'lucide-react';
import i18n from '../../app/providers/i18n';
import { logger } from '../../lib/logger';

// Inline SVG markup for the refresh icon, rendered once at module load so it
// can be injected into innerHTML overlays without React. Matches the previous
// emoji glyph size (48px).
const REFRESH_ICON_SVG = renderToStaticMarkup(createElement(RefreshCw, { size: 48 }));

interface ReconnectFailedDeps {
  onRetry: () => void;
}

let activeReconnectFailedHost: HTMLElement | null = null;

/**
 * Terminal reconnect state: shown once the automatic reconnect loop has
 * exhausted its attempt budget. Reconnecting resumes only through the
 * explicit user action.
 */
export function showReconnectFailedDialog(deps: ReconnectFailedDeps): void {
  if (activeReconnectFailedHost) return;
  try {
    const host = document.createElement('div');
    host.style.cssText =
      'position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.5);';
    host.innerHTML = `
      <div style="min-width:320px;max-width:480px;padding:24px;border-radius:12px;border:1px solid rgba(239,68,68,0.5);background:rgba(30,41,59,0.95);backdrop-filter:blur(8px);color:#fff;box-shadow:0 8px 32px rgba(0,0,0,0.4);text-align:center;">
        <div style="display:flex;justify-content:center;margin-bottom:12px;">${REFRESH_ICON_SVG}</div>
        <div style="font-weight:700;font-size:18px;margin-bottom:8px;">${i18n.t('realtime.reconnectFailed.title')}</div>
        <div style="font-size:14px;color:#94a3b8;margin-bottom:20px;">${i18n.t('realtime.reconnectFailed.description')}</div>
        <button data-reconnect-retry style="padding:10px 20px;border-radius:8px;border:none;background:#3b82f6;color:white;cursor:pointer;font-weight:600;font-size:14px;">${i18n.t('realtime.reconnectFailed.retry')}</button>
      </div>`;
    document.body.appendChild(host);
    activeReconnectFailedHost = host;
    host.querySelector('[data-reconnect-retry]')?.addEventListener(
      'click',
      () => {
        try {
          host.remove();
        } catch {}
        if (activeReconnectFailedHost === host) activeReconnectFailedHost = null;
        deps.onRetry();
      },
      { once: true },
    );
  } catch {}
}

interface ServerRestartDeps {
  apiBase: string;
  /** Caller hook used to set disposed=true so no further reconnect attempt runs. */
  onRestartDetected: () => void;
}

export function showServerRestartDialog(deps: ServerRestartDeps): void {
  logger.info('[useWorldRoom] Server restart notification received');
  deps.onRestartDetected();

  const host = document.createElement('div');
  host.style.cssText =
    'position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.6);';
  host.innerHTML = `
    <div style="min-width:320px;max-width:480px;padding:32px;border-radius:16px;border:1px solid rgba(59,130,246,0.4);background:rgba(15,23,42,0.97);backdrop-filter:blur(12px);color:#fff;box-shadow:0 8px 32px rgba(0,0,0,0.5);text-align:center;">
      <div style="margin-bottom:16px;">
        <div style="width:48px;height:48px;margin:0 auto;border:3px solid rgba(59,130,246,0.3);border-top-color:#3b82f6;border-radius:50%;animation:spin 1s linear infinite;"></div>
        <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
      </div>
      <div style="font-weight:700;font-size:18px;margin-bottom:8px;">${i18n.t('serverRestart.title')}</div>
      <div data-restart-desc style="font-size:14px;color:#94a3b8;margin-bottom:20px;">${i18n.t('serverRestart.description')}</div>
      <button data-restart-reload style="display:none;padding:10px 24px;border-radius:8px;border:none;background:#3b82f6;color:white;cursor:pointer;font-weight:600;font-size:14px;">${i18n.t('serverRestart.reloadButton')}</button>
    </div>`;
  document.body.appendChild(host);

  let attempts = 0;
  const maxAttempts = 30;
  const pollHealth = setInterval(() => {
    void (async () => {
      attempts++;
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 3000);
        const res = await fetch(deps.apiBase + '/health', { signal: controller.signal });
        clearTimeout(timeout);
        if (res.ok) {
          clearInterval(pollHealth);
          window.location.reload();
          return;
        }
      } catch {
        /* server not yet back */
      }

      if (attempts >= maxAttempts) {
        clearInterval(pollHealth);
        const desc = host.querySelector('[data-restart-desc]');
        const btn: HTMLElement | null = host.querySelector('[data-restart-reload]');
        if (desc) desc.textContent = i18n.t('serverRestart.manualReload');
        if (btn) btn.style.display = 'inline-block';
      }
    })();
  }, 2000);

  host.querySelector('[data-restart-reload]')?.addEventListener(
    'click',
    () => {
      window.location.reload();
    },
    { once: true },
  );
}
